import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProtectedRoute from "./routes/ProtectedRoute";
import PrivateRoute from "./routes/PrivateRoute";

// Layouts
import MainLayout from "./layouts/MainLayout";

// Pages
import Login from "./pages/auth/Login";
import StartSession from "./pages/start session/StartSession";
import HomePage from "./pages/home/HomePage";
import Invoices from "./pages/invoice/Invoices";
import Sales from "./pages/sales/SessionSales";
import QRSubscription from "./pages/qr-subscription/QRSubscription";
import Cart from "./pages/cart/Cart";
import InvoiceDetails from "./pages/invoice/components/InvoiceDetails";
import ErrorFallback from "./pages/error 404/ErrorFallback";
import OrdersPage from "./pages/orders/OrdersPage";
import NearpayReceiptsPage from "./pages/receipts/NearpayReceiptsPage";
import usePrefetchStoreData from "./hooks/usePrefetchStoreData";
import useNetworkOptimization from "./hooks/useNetworkOptimization";
import usePerformanceOptimization from "./hooks/usePerformanceOptimization";
import useWebVitals from "./hooks/useWebVitals";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

function OptimizedWrapper() {
  usePrefetchStoreData();
  useNetworkOptimization();

  usePerformanceOptimization({
    enableVirtualScrolling: true,
    enableImageOptimization: true,
    enableMemoryManagement: true,
    enableBatteryOptimization: true,
    enableNetworkAwareness: true,
    maxCacheSize: 100,
    cleanupInterval: 3 * 60 * 1000,
  });

  useWebVitals("Netlos POS App");

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <OptimizedWrapper />
      <Router>
        <Routes>
          {/* Public */}
          <Route path='/login' element={<Login />} />
          <Route
            path='/start-session'
            element={
              <ProtectedRoute>
                <StartSession />
              </ProtectedRoute>
            }
          />
          {/* Main Layout (after start session) */}
          <Route
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            <Route path='/' element={<HomePage />} />
            <Route path='/invoices' element={<Invoices />} />
            <Route path='/invoices/:id' element={<InvoiceDetails />} />
            <Route path='/sales' element={<Sales />} />
            <Route path='/qr-subscription' element={<QRSubscription />} />
            <Route path='/cart' element={<Cart />} />
            <Route path='/orders' element={<OrdersPage />} />
            <Route path='/receipts' element={<NearpayReceiptsPage />} />
          </Route>
          <Route path='*' element={<ErrorFallback type='not-found' />} />
        </Routes>

        <ToastContainer position='top-right' rtl={true} />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
